/**
 * Error logging service for the Merchant Portal
 * Provides centralized error logging with context information
 */

export interface ErrorContext {
  userId?: string;
  action?: string;
  component?: string;
  url?: string;
  userAgent?: string;
  timestamp?: string;
  [key: string]: any;
}

export interface LoggedError {
  message: string;
  stack?: string;
  context: ErrorContext;
  timestamp: string;
  level: 'error' | 'warning' | 'info';
}

class ErrorLogger {
  private logs: LoggedError[] = [];
  private maxLogs = 100; // Keep last 100 logs in memory

  /**
   * Log an error with context information
   */
  logError(
    error: Error | string,
    context: ErrorContext = {},
    level: 'error' | 'warning' | 'info' = 'error'
  ): void {
    const errorMessage = typeof error === 'string' ? error : error.message;
    const errorStack = typeof error === 'string' ? undefined : error.stack;

    const logEntry: LoggedError = {
      message: errorMessage,
      stack: errorStack,
      context: {
        ...context,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      },
      timestamp: new Date().toISOString(),
      level,
    };

    // Add to in-memory logs
    this.logs.push(logEntry);

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Console logging based on level
    switch (level) {
      case 'error':
        console.error('Error logged:', {
          message: errorMessage,
          stack: errorStack,
          context: logEntry.context,
        });
        break;
      case 'warning':
        console.warn('Warning logged:', {
          message: errorMessage,
          context: logEntry.context,
        });
        break;
      case 'info':
        console.info('Info logged:', {
          message: errorMessage,
          context: logEntry.context,
        });
        break;
    }

    // In production, send to error tracking service
    if (import.meta.env.PROD && level === 'error') {
      this.sendToErrorTrackingService(logEntry);
    }
  }

  /**
   * Log a network error with retry information
   */
  logNetworkError(error: Error, url: string, retryCount: number = 0): void {
    this.logError(error, {
      action: 'network_request',
      url,
      retryCount,
      component: 'api_client',
    });
  }

  /**
   * Log a validation error
   */
  logValidationError(fieldName: string, value: any, rule: string, component: string): void {
    this.logError(
      `Validation failed for field ${fieldName}`,
      {
        action: 'form_validation',
        fieldName,
        value: typeof value === 'object' ? JSON.stringify(value) : String(value),
        rule,
        component,
      },
      'warning'
    );
  }

  /**
   * Log user action for debugging
   */
  logUserAction(action: string, component: string, data?: any): void {
    this.logError(
      `User action: ${action}`,
      {
        action: 'user_interaction',
        component,
        data: data ? JSON.stringify(data) : undefined,
      },
      'info'
    );
  }

  /**
   * Get recent logs for debugging
   */
  getRecentLogs(count: number = 10): LoggedError[] {
    return this.logs.slice(-count);
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Send error to external tracking service (placeholder for production)
   */
  private sendToErrorTrackingService(logEntry: LoggedError): void {
    // In a real application, this would send to services like:
    // - Sentry
    // - LogRocket
    // - Datadog
    // - Custom logging endpoint

    // For now, we'll just store it locally for development
    try {
      const existingLogs = localStorage.getItem('error_logs');
      const logs = existingLogs ? JSON.parse(existingLogs) : [];
      logs.push(logEntry);

      // Keep only last 50 logs in localStorage
      const recentLogs = logs.slice(-50);
      localStorage.setItem('error_logs', JSON.stringify(recentLogs));
    } catch (e) {
      console.error('Failed to store error log:', e);
    }
  }
}

// Export singleton instance
export const errorLogger = new ErrorLogger();

// Convenience functions
export const logError = (error: Error | string, context?: ErrorContext) =>
  errorLogger.logError(error, context);

export const logNetworkError = (error: Error, url: string, retryCount?: number) =>
  errorLogger.logNetworkError(error, url, retryCount);

export const logValidationError = (
  fieldName: string,
  value: any,
  rule: string,
  component: string
) => errorLogger.logValidationError(fieldName, value, rule, component);

export const logUserAction = (action: string, component: string, data?: any) =>
  errorLogger.logUserAction(action, component, data);
